import "./DeleteSectionModal.css";

import { overlay } from "overlay-kit";
import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { store, useAppSelector } from "store";
import { sectionSelectors } from "store/selectors";
import { selectCourseCodeBySectionId } from "store/selectors/section";

export const showUnlinkConfirmationModal = ({
  sectionId,
  templateTitle,
}: {
  sectionId: string;
  templateTitle: string;
}) => {
  const section = sectionSelectors.selectById(store.getState(), sectionId);
  if (!section) {
    return Promise.resolve(false);
  }

  return overlay.openAsync<boolean>(({ isOpen, close }) => (
    <UnlinkSectionModal
      open={isOpen}
      sectionId={sectionId}
      sectionTitle={section.title}
      templateTitle={templateTitle}
      onClose={close}
      onConfirm={() => {}}
    />
  ));
};

type UnlinkSectionModalProps = {
  sectionId: string;
  sectionTitle: string;
  templateTitle: string;
  open: boolean;
  onConfirm: () => void;
  onClose: (confirmed: boolean) => void;
};

type UnlinkSectionFormValues = {
  courseCodeConfirmation: string;
};

// eslint-disable-next-line react-refresh/only-export-components
const UnlinkSectionModal: FC<UnlinkSectionModalProps> = ({
  sectionId,
  sectionTitle,
  templateTitle,
  open,
  onConfirm,
  onClose,
}) => {
  const unlinkSectionTitle = t("sidebar_options_menu.unlink_confirm_button");

  const courseCode = useAppSelector((state) => selectCourseCodeBySectionId(state, sectionId));

  const form = useForm<UnlinkSectionFormValues>({
    defaultValues: {
      courseCodeConfirmation: "",
    },
  });

  const courseCodeConfirmation = form.watch("courseCodeConfirmation");

  return (
    <ConfirmationDialog
      confirmButtonDisabled={courseCodeConfirmation !== courseCode}
      confirmButtonKind="caution"
      confirmButtonText={unlinkSectionTitle}
      description={t("sidebar_options_menu.unlink_confirm_label", { name: sectionTitle })}
      open={open}
      title={unlinkSectionTitle}
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <FormProvider {...form}>
        <div className="section-delete-modal mt-8 flex flex-col items-start gap-8">
          <div className="section-delete-warning">
            {t("sidebar_options_menu.unlink_confirm_description", { template: templateTitle })}
          </div>

          <FormField
            className="w-full"
            label={t("section_list_panel.course_confirm_label")}
            name="courseCodeConfirmation"
            required
          >
            {(controlProps) => (
              <TextField
                {...form.register("courseCodeConfirmation")}
                {...controlProps}
                placeholder={t("section_list_panel.course_placeholder")}
              />
            )}
          </FormField>
        </div>
      </FormProvider>
    </ConfirmationDialog>
  );
};
